import React from 'react';
import styled from 'styled-components';
import {
	Button,
	ButtonDropdownField,
	DateRangePicker,
	FlexRow,
	Icon,
	Tooltip,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {BookmarkIcon, BOOKMARK_COLOR_DISABLED} from '../../shared/icons/BookmarkIcon';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';

const tokens = tokensForThemeProvider.sky.sys;

/** Matches the dwell time every other saved-report toast in the app uses, so save feedback here
 * reads the same as saving a Standard report. */
const SAVE_TOAST_DURATION_MS = 5000;

/* Anchors the hint under the Save button. The hint is taken out of flow (below) so this wrapper keeps
 * the button's own height, leaving Save on the same baseline as Download and Share instead of being
 * pushed up by the text underneath it. */
const SaveAnchor = styled.div`
	position: relative;
	display: inline-flex;
`;

/* The hint reads as a blocking condition, so it takes the semantic error text colour rather than an
 * auxiliary grey, at the smallest detail step so it stays subordinate to the button it explains. */
const SaveHint = styled.span`
	${tokens.font.detail['2']}
	position: absolute;
	top: calc(100% + ${tokens.spacing['100']});
	right: 0;
	color: ${tokens.color.text.semantic.error};
	white-space: nowrap;
`;

/* the design: shows the date-range placeholder uppercase (DD/MM/YYYY). The DS
 * DateRangePicker's react-aria segments render lowercase (mm/dd/yyyy) with no format prop;
 * text-transform inherits into the segment spans, uppercasing the placeholder to MM/DD/YYYY
 * without forking the component. Segment ORDER stays locale-driven (mm/dd for en-US), which is
 * correct per-locale — only the case is normalised to match the design. */

const UppercaseDateWrapper = styled.div`
	text-transform: uppercase;
`;

type DatePickerDate = string | null;

interface DateRangeChangeEvent {
	startDate: DatePickerDate;
	endDate: DatePickerDate;
}

/** Top bar shown in edit mode: the reporting-period date range, a disabled single-button Download
 * (neutral field style with an inline chevron) / Share (both stubbed with a tooltip), and a Save Report
 * button wired to `useReportBuilder().save()`. Changing the period rewrites the definition's date
 * filter, so the table re-runs for the new window. The report name lives in `ReportPageHeader`
 * (rendered above this bar in `ReportBuilderSurface`), not here. */
export function EditTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition, dirty, save, saving, nameChanged, period, setPeriod, setMode} = useReportBuilder();

	const handlePeriodChange = (range: DateRangeChangeEvent): void => {
		setPeriod(range.startDate, range.endDate);
	};

	// Per the design, Save Report both persists the draft AND returns the surface to view mode (it does
	// not just persist and stay in edit mode) — but only once the report is actually in the Reports
	// list. A failed save keeps the surface in edit mode with the draft intact and Save still enabled,
	// and reports the failure in a warning toast; switching to view mode there would read as success.
	const handleSave = (): void => {
		void save().then(saved => {
			if (!saved) {
				createToast({
					duration: SAVE_TOAST_DURATION_MS,
					warning: true,
					message: formatMessage({id: 'ai_report_builder.save_failed'}),
				});
				return;
			}
			createToast({
				duration: SAVE_TOAST_DURATION_MS,
				message: formatMessage({id: 'common.action.saved'}, {entity: definition.name}),
			});
			setMode('view');
		});
	};

	// Save needs unsaved edits AND a name of the report's own: the draft arrives named by the agent, and
	// saving that through would fill the Reports list with generated names. Never offered while a save is
	// in flight either — a second click during the first would create a second Reports-list entry for the
	// same draft.
	const canSave = dirty && nameChanged && !saving;

	const stubTooltip = formatMessage({id: 'ai_report_builder.stub_action_tooltip'});

	return (
		<FlexRow justifyContent={'space-between'} alignItems={'center'} data-cy={'ai-report-edit-top-bar'}>
			{/* The toolbar is space-between: the date range hugs the left, the actions hug the right. DS
			 * FlexRow defaults to flex:1, so both groups would each take 50% and drift to mid-row; pin both
			 * to flex:0 0 auto so they hug their content and space-between pushes them to the true edges. */}
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<UppercaseDateWrapper>
					<DateRangePicker
						aria-label={formatMessage({id: 'ai_report_builder.period'})}
						data-cy={'ai-report-period-picker'}
						startDate={period.startDate}
						endDate={period.endDate}
						onChange={handlePeriodChange}
					/>
				</UppercaseDateWrapper>
			</FlexRow>
			<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
				<Tooltip content={stubTooltip}>
					<span>
						{/* the design: Download is a SINGLE neutral field-style button — leading
						 * download glyph, "Download" label, inline trailing chevron — NOT a split button
						 * with a detached caret section (which the design does not have). Rendered
						 * disabled (stub; real download behaviour lands in a later ticket).
						 * ButtonDropdownField supplies the neutral border + built-in trailing chevron. */}
						<ButtonDropdownField
							width={'auto'}
							disabled
							aria-label={formatMessage({id: 'ai_report_builder.download'})}
							data-cy={'ai-report-download-button'}
						>
							<FlexRow alignItems={'center'} gap={'xs'}>
								<Icon icon={'download'} size={'s'} />
								<FormattedMessage id={'ai_report_builder.download'} />
							</FlexRow>
						</ButtonDropdownField>
					</span>
				</Tooltip>
				<Tooltip content={stubTooltip}>
					<span>
						<Button
							emphasis={'medium'}
							icon={'share'}
							disabled
							data-cy={'ai-report-share-button'}
							aria-label={formatMessage({id: 'ai_report_builder.share'})}
						>
							{formatMessage({id: 'ai_report_builder.share'})}
						</Button>
					</span>
				</Tooltip>
				<SaveAnchor>
					<Button emphasis={'high'} disabled={!canSave} onPress={handleSave} data-cy={'ai-report-save-button'}>
						{/* the design: Save Report carries a leading bookmark glyph. The DS icon set
						 * has no bookmark; reuse the shared BookmarkIcon (shared/icons/BookmarkIcon) that
						 * the agent chat also uses for "Your Saved Prompts". White on the enabled dark-blue high-emphasis
						 * button; the muted token when disabled (clean) so it reads with the greyed label. */}
						<FlexRow alignItems={'center'} gap={'xs'}>
							<BookmarkIcon color={canSave ? '#ffffff' : BOOKMARK_COLOR_DISABLED} />
							<FormattedMessage id={'ai_report_builder.save_report'} />
						</FlexRow>
					</Button>
					{/* Says WHY Save is unavailable, rather than leaving a disabled button unexplained. Only the
					 * unchanged-name reason gets a hint: a draft with no edits at all needs no prompting, and the
					 * in-flight case resolves on its own. */}
					{!nameChanged ? (
						<SaveHint data-cy={'ai-report-save-hint'}>
							{formatMessage({id: 'ai_report_builder.save_needs_name'})}
						</SaveHint>
					) : null}
				</SaveAnchor>
			</FlexRow>
		</FlexRow>
	);
}
